<template>
  <div>
    <div
      v-if="error && !allRows.length"
      class="alert alert-danger"
    >
      {{ error }}
    </div>
    <div
      v-if="allRows.length"
      class="block block-mode-loading-oneui"
    >
      <div class="block-header border-bottom">
        <h3 class="block-title">
          Team Targets
          <small class="d-block text-muted fw-normal">{{ rangeSubtitle }}</small>
          <small class="text-info">
            <template v-if="hiddenCount">{{ rows.length }} / {{ allRows.length }}</template>
            <template v-else>{{ rows.length }}</template>
            staff
          </small>
        </h3>
        <div class="block-options d-flex align-items-center flex-wrap">
          <div
            v-if="loading && data"
            class="spinner-border spinner-border-sm text-primary me-2"
            role="status"
          >
            <span class="visually-hidden">Loading...</span>
          </div>
          <div class="d-flex flex-column me-2">
            <div class="d-flex align-items-center">
              <label
                class="me-1 mb-0 fs-sm text-muted"
                for="team-targets-from"
              >From</label>
              <input
                id="team-targets-from"
                v-model="draftFrom"
                type="date"
                class="form-control form-control-sm"
                style="width: auto;"
                :min="fromMin || undefined"
                :max="fromMax"
              >
              <label
                class="ms-2 me-1 mb-0 fs-sm text-muted"
                for="team-targets-to"
              >To</label>
              <input
                id="team-targets-to"
                v-model="draftTo"
                type="date"
                class="form-control form-control-sm"
                style="width: auto;"
                :min="draftFrom || undefined"
                :max="today"
              >
              <button
                v-if="isDirty"
                type="button"
                class="btn btn-sm btn-alt-secondary ms-2"
                :disabled="applyDisabled"
                @click="applyRange"
              >
                Apply
              </button>
              <button
                v-if="rangeMode || draftFrom || draftTo"
                type="button"
                class="btn btn-sm btn-alt-secondary ms-1"
                @click="resetRange"
              >
                Reset
              </button>
            </div>
            <div
              v-if="draftError"
              class="fs-sm text-danger"
            >
              {{ draftError }}
            </div>
            <div
              v-else-if="isDirty"
              class="fs-sm text-muted"
            >
              Not applied
            </div>
          </div>
          <button
            type="button"
            class="btn btn-sm btn-alt-secondary me-1"
            :aria-expanded="showFilter ? 'true' : 'false'"
            @click="showFilter = !showFilter"
          >
            <i class="fa fa-filter me-1" />
            Members
            <span
              v-if="hiddenCount"
              class="badge bg-warning ms-1"
              :title="`${hiddenCount} hidden`"
            >{{ hiddenCount }} hidden</span>
          </button>
          <button
            type="button"
            class="btn btn-sm btn-alt-secondary"
            @click="toggleAll"
          >
            {{ allExpanded ? 'Collapse all' : 'Expand all' }}
          </button>
        </div>
      </div>
      <div
        v-if="showFilter"
        class="block-content block-content-full border-bottom bg-body-light"
      >
        <div class="d-flex align-items-center mb-2">
          <span class="fw-semibold me-3">Show members</span>
          <button
            type="button"
            class="btn btn-sm btn-link p-0 me-3"
            @click="showAllMembers"
          >
            Show all
          </button>
          <button
            type="button"
            class="btn btn-sm btn-link p-0"
            @click="hideAllMembers"
          >
            Hide all
          </button>
        </div>
        <input
          v-model="memberQuery"
          type="search"
          class="form-control form-control-sm mb-3"
          style="max-width: 20rem;"
          placeholder="Search name or role"
          aria-label="Search members"
          autocomplete="off"
        >
        <p
          v-if="!filteredMembers.length"
          class="text-muted mb-0"
        >
          No members match
        </p>
        <div
          v-else
          class="row"
        >
          <div
            v-for="row in filteredMembers"
            :key="row.user_id"
            class="col-sm-6 col-lg-4"
          >
            <div class="form-check">
              <input
                :id="`team-target-member-${row.user_id}`"
                class="form-check-input"
                type="checkbox"
                :checked="!isHidden(row)"
                @change="setHidden(row, !$event.target.checked)"
              >
              <label
                class="form-check-label"
                :for="`team-target-member-${row.user_id}`"
              >
                {{ row.name }}
                <small class="text-muted">{{ row.role }}</small>
              </label>
            </div>
          </div>
        </div>
      </div>
      <div class="block-content block-content-full">
        <div
          v-if="error"
          class="alert alert-danger"
        >
          Couldn't load {{ failedRangeLabel }}: {{ error }}. Showing {{ appliedRangeLabel }}.
        </div>
        <div class="position-relative">
          <div
            class="table-responsive"
            :style="{ opacity: loading ? 0.4 : 1, transition: 'opacity 0.2s ease' }"
          >
            <table class="table table-sm table-vcenter mb-0">
              <thead>
                <tr>
                  <th>Staff</th>
                  <th
                    v-for="metric in metrics"
                    :key="metric.key"
                    class="text-end"
                  >
                    {{ rangeMode ? (metric.rangeLabel || metric.shortLabel) : metric.shortLabel }}
                    <span
                      v-if="!rangeMode && targets[metric.key] !== null && targets[metric.key] !== undefined"
                      class="text-info fw-normal"
                    >/ {{ targets[metric.key] }}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!rows.length">
                  <td
                    :colspan="metrics.length + 1"
                    class="text-center text-muted"
                  >
                    All members hidden
                  </td>
                </tr>
                <tr
                  v-for="item in visibleRows"
                  :key="item.row.user_id"
                >
                  <td :style="{ paddingLeft: (0.5 + item.depth * 1.25) + 'rem' }">
                    <button
                      v-if="hasChildren(item.row)"
                      type="button"
                      class="btn btn-sm btn-link p-0 me-1"
                      :aria-expanded="isExpanded(item.row) ? 'true' : 'false'"
                      @click="toggle(item.row)"
                    >
                      <i :class="isExpanded(item.row) ? 'fa fa-caret-down' : 'fa fa-caret-right'" />
                    </button>
                    <a
                      class="fw-semibold"
                      target="_blank"
                      :href="`/admin/staff/view/${item.row.user_id}`"
                    >{{ item.row.name }}</a>
                    <div class="fs-sm text-info">
                      {{ item.row.role }}
                      <template v-if="hasChildren(item.row)">
                        · {{ descendantCount(item.row) }} {{ hiddenCount ? 'visible in team' : 'in team' }}
                      </template>
                    </div>
                  </td>
                  <td
                    v-for="metric in metrics"
                    :key="metric.key"
                    class="text-end"
                  >
                    <span :class="metClass(metric.key, item.row.own[metric.key])">
                      {{ format(metric.key, item.row.own[metric.key]) }}
                    </span>
                    <div
                      v-if="teamTotalsByUserId[item.row.user_id]"
                      class="fs-sm text-info"
                      :title="hiddenCount ? 'Own + whole visible team' : 'Own + whole team'"
                    >
                      Team: {{ format(metric.key, teamTotalsByUserId[item.row.user_id][metric.key]) }}
                    </div>
                  </td>
                </tr>
              </tbody>
              <tfoot v-if="rows.length">
                <tr class="table-light">
                  <th>Total</th>
                  <th
                    v-for="metric in metrics"
                    :key="metric.key"
                    class="text-end"
                  >
                    {{ format(metric.key, totals[metric.key]) }}
                  </th>
                </tr>
              </tfoot>
            </table>
          </div>
          <div
            v-if="loading"
            class="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          >
            <div
              class="spinner-grow text-primary"
              role="status"
            >
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { STAFF_TARGET_METRICS, formatMetric } from './staffTargetMetrics'

function utcDay(iso) {
  const [year, month, day] = iso.split('-').map(Number)
  return Date.UTC(year, month - 1, day)
}

function inclusiveDayCount(from, to) {
  return Math.round((utcDay(to) - utcDay(from)) / 86400000) + 1
}

function shiftIsoDate(iso, days) {
  const date = new Date(utcDay(iso))
  date.setUTCDate(date.getUTCDate() + days)
  return date.toISOString().slice(0, 10)
}

const HIDDEN_STORAGE_KEY = 'dashboard.teamTargets.hidden'

export default {
  name: 'TeamTargetsTable',

  props: {
    // Logged-in staff user id; scopes the saved hidden list so shared browsers don't mix managers' choices.
    userId: {
      type: Number,
      default: 0
    }
  },

  data() {
    return {
      metrics: STAFF_TARGET_METRICS,
      expanded: {},
      // user_id => true for members the viewer chose to hide; persisted in localStorage
      hidden: {},
      showFilter: false,
      memberQuery: '',
      draftFrom: '',
      draftTo: '',
      failedRangeLabel: 'today / this month'
    }
  },

  computed: {
    ...mapGetters('dashboard', {
      data: 'teamTargets',
      loading: 'teamTargetsLoading',
      error: 'teamTargetsError'
    }),

    rangeMode() {
      return !!(this.data && this.data.range)
    },

    today() {
      return this.data ? this.data.today : ''
    },

    maxRangeDays() {
      const value = this.data && this.data.max_range_days
      return Number(value) || 0
    },

    appliedFrom() {
      return this.data && this.data.range ? (this.data.from || '') : ''
    },

    appliedTo() {
      return this.data && this.data.range ? (this.data.to || '') : ''
    },

    isDirty() {
      return this.draftFrom !== this.appliedFrom || this.draftTo !== this.appliedTo
    },

    oneDraftMissing() {
      return (this.draftFrom && !this.draftTo) || (!this.draftFrom && this.draftTo)
    },

    draftError() {
      if (!this.draftFrom && !this.draftTo) {
        return ''
      }
      if (this.oneDraftMissing) {
        return 'Pick both dates'
      }
      if (this.draftFrom > this.draftTo) {
        return 'From must be on or before To'
      }
      if (this.today && this.draftTo > this.today) {
        return "To can't be in the future"
      }
      if (this.maxRangeDays && inclusiveDayCount(this.draftFrom, this.draftTo) > this.maxRangeDays) {
        return `Max ${this.maxRangeDays} days`
      }
      return ''
    },

    applyDisabled() {
      return this.loading || !!this.draftError || this.oneDraftMissing
    },

    fromMax() {
      return this.draftTo || this.today
    },

    fromMin() {
      if (!this.draftTo || !this.maxRangeDays) {
        return ''
      }
      return shiftIsoDate(this.draftTo, -(this.maxRangeDays - 1))
    },

    appliedRangeLabel() {
      if (this.rangeMode && this.data.from && this.data.to) {
        return `${this.data.from} → ${this.data.to}`
      }
      return 'today / this month'
    },

    rangeSubtitle() {
      if (this.rangeMode && this.data.from && this.data.to) {
        return `${this.data.from} → ${this.data.to} (${this.data.timezone})`
      }
      return 'Today / this month'
    },

    allRows() {
      return (this.data && this.data.team) || []
    },

    // Rows the viewer has not hidden; everything below (tree, totals) works off these.
    rows() {
      return this.allRows.filter(row => !this.hidden[row.user_id])
    },

    sortedAllRows() {
      return [...this.allRows].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    },

    filteredMembers() {
      const query = this.memberQuery.trim().toLowerCase()
      if (!query) {
        return this.sortedAllRows
      }
      return this.sortedAllRows.filter(row => {
        const name = (row.name || '').toLowerCase()
        const role = (row.role || '').toLowerCase()
        return name.includes(query) || role.includes(query)
      })
    },

    hiddenCount() {
      return this.allRows.length - this.rows.length
    },

    storageKey() {
      return this.userId ? `${HIDDEN_STORAGE_KEY}.${this.userId}` : HIDDEN_STORAGE_KEY
    },

    targets() {
      return (this.data && this.data.targets) || {}
    },

    // Sum of every visible staff member's own counts (not team totals, which would double count subtrees).
    totals() {
      const totals = {}
      this.metrics.forEach(metric => {
        totals[metric.key] = this.rows.reduce((sum, row) => this.addMetric(metric.key, sum, (row.own || {})[metric.key]), 0)
      })
      return totals
    },

    allRowsByUserId() {
      const map = {}
      this.allRows.forEach(row => { map[row.user_id] = row })
      return map
    },

    // user_id => nearest visible manager's user_id (skipping hidden managers), or null for roots.
    parentByUserId() {
      const map = {}
      this.rows.forEach(row => {
        const seen = { [row.user_id]: true }
        let parentId = row.parent_user_id
        map[row.user_id] = null
        while (parentId !== null && parentId !== undefined && !seen[parentId]) {
          const parent = this.allRowsByUserId[parentId]
          if (!parent) {
            break
          }
          if (!this.hidden[parentId]) {
            map[row.user_id] = parentId
            break
          }
          seen[parentId] = true
          parentId = parent.parent_user_id
        }
      })
      return map
    },

    childrenByUserId() {
      const map = {}
      this.rows.forEach(row => {
        const parentId = this.parentByUserId[row.user_id]
        if (parentId !== null) {
          (map[parentId] = map[parentId] || []).push(row)
        }
      })
      return map
    },

    // Rows whose manager is not in the visible list (the viewer's direct reports, the top of the org for Super Admin,
    // or members whose whole management chain is hidden).
    roots() {
      return this.rows.filter(row => this.parentByUserId[row.user_id] === null)
    },

    // Roots, plus any rows caught in a parent_id loop (they never hang off a root).
    topLevelRows() {
      const reachable = {}
      const stack = [...this.roots]
      while (stack.length) {
        const row = stack.pop()
        if (reachable[row.user_id]) {
          continue
        }
        reachable[row.user_id] = true
        stack.push(...(this.childrenByUserId[row.user_id] || []))
      }
      return this.roots.concat(this.rows.filter(row => !reachable[row.user_id]))
    },

    visibleRows() {
      const result = []
      const seen = {}
      const walk = (row, depth) => {
        if (seen[row.user_id]) {
          return
        }
        seen[row.user_id] = true
        result.push({ row, depth })
        if (this.isExpanded(row)) {
          (this.childrenByUserId[row.user_id] || []).forEach(child => walk(child, depth + 1))
        }
      }
      this.topLevelRows.forEach(row => walk(row, 0))
      return result
    },

    teamTotalsByUserId() {
      if (!this.hiddenCount) {
        const map = {}
        this.rows.forEach(row => {
          if (row.team_total) {
            map[row.user_id] = row.team_total
          }
        })
        return map
      }

      const map = {}
      this.managerIds.forEach(id => {
        const manager = this.allRowsByUserId[id]
        const members = [manager, ...this.descendants(manager)]
        const totals = {}
        this.metrics.forEach(metric => {
          totals[metric.key] = members.reduce((sum, row) => this.addMetric(metric.key, sum, (row.own || {})[metric.key]), 0)
        })
        map[id] = totals
      })
      return map
    },

    managerIds() {
      return Object.keys(this.childrenByUserId)
    },

    allExpanded() {
      return this.managerIds.length > 0 && this.managerIds.every(id => this.expanded[id] !== false)
    }
  },

  mounted() {
    this.loadHidden()
    this.fetchData()
  },

  methods: {
    ...mapActions('dashboard', ['fetchTeamTargets']),

    async fetchData({ from = null, to = null } = {}) {
      this.failedRangeLabel = (from && to) ? `${from} → ${to}` : 'today / this month'
      try {
        await this.fetchTeamTargets({
          from: from || null,
          to: to || null
        })
      } catch (error) {
        console.error('Failed to fetch team targets:', error)
      }
    },

    applyRange() {
      this.fetchData({ from: this.draftFrom, to: this.draftTo })
    },

    resetRange() {
      const shouldRefetch = this.rangeMode || this.loading
      this.draftFrom = ''
      this.draftTo = ''
      if (shouldRefetch) {
        this.fetchData()
      }
    },

    loadHidden() {
      try {
        const ids = JSON.parse(window.localStorage.getItem(this.storageKey) || '[]')
        const hidden = {}
        if (Array.isArray(ids)) {
          ids.forEach(id => { hidden[id] = true })
        }
        this.hidden = hidden
      } catch (error) {
        this.hidden = {}
      }
    },

    saveHidden() {
      try {
        window.localStorage.setItem(this.storageKey, JSON.stringify(Object.keys(this.hidden).map(Number)))
      } catch (error) {
        // Storage unavailable (private mode / blocked); the choice just won't persist.
      }
    },

    isHidden(row) {
      return !!this.hidden[row.user_id]
    },

    setHidden(row, hide) {
      if (hide) {
        this.$set(this.hidden, row.user_id, true)
      } else {
        this.$delete(this.hidden, row.user_id)
      }
      this.saveHidden()
    },

    showAllMembers() {
      this.hidden = {}
      this.saveHidden()
    },

    hideAllMembers() {
      const hidden = { ...this.hidden }
      this.allRows.forEach(row => { hidden[row.user_id] = true })
      this.hidden = hidden
      this.saveHidden()
    },

    hasChildren(row) {
      return !!this.childrenByUserId[row.user_id]
    },

    isExpanded(row) {
      return this.expanded[row.user_id] !== false
    },

    toggle(row) {
      this.$set(this.expanded, row.user_id, !this.isExpanded(row))
    },

    toggleAll() {
      const expand = !this.allExpanded
      const next = {}
      if (!expand) {
        this.managerIds.forEach(id => { next[id] = false })
      }
      this.expanded = next
    },

    // Every visible row below the given one (loop-guarded).
    descendants(row) {
      const result = []
      const seen = { [row.user_id]: true }
      const stack = [...(this.childrenByUserId[row.user_id] || [])]
      while (stack.length) {
        const child = stack.pop()
        if (seen[child.user_id]) {
          continue
        }
        seen[child.user_id] = true
        result.push(child)
        stack.push(...(this.childrenByUserId[child.user_id] || []))
      }
      return result
    },

    descendantCount(row) {
      return this.descendants(row).length
    },

    addMetric(key, sum, value) {
      const amount = Number(value) || 0
      if (key === 'credits') {
        return Math.round((sum + amount) * 100) / 100
      }
      return sum + amount
    },

    metClass(key, value) {
      if (this.rangeMode) {
        return 'fw-semibold'
      }
      const target = this.targets[key]
      if (target === null || target === undefined) {
        return 'fw-semibold'
      }
      return (value || 0) >= target ? 'fw-bold text-success' : 'fw-semibold'
    },

    format(key, value) {
      return formatMetric(key, value)
    }
  }
}
</script>
